import React, { useCallback, useMemo } from "react";
import { AccountDetailsAccountType, AccountDetailsInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { OptionNameValuePair } from "../../../../../../src/lib/interfaces/types";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addAccountDetailsRow,
  deleteAccountDetailsRow,
  selectFMR1DraftAccountDetailsRows,
  updateAccountDetailsRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type AccountDetailsTableHandle = {
  getDirtyRows: () => AccountDetailsInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => AccountDetailsInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: AccountDetailsInfoRow[]) => void;
};

export interface AccountDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: AccountDetailsInfoRow[];
}

const buildEmptyRow = (): AccountDetailsInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

const ACCOUNT_TYPE_OPTIONS: OptionNameValuePair<AccountDetailsAccountType>[] = Object.values(
  AccountDetailsAccountType,
).map((v) => ({
  name: v,
  value: v,
}));

export const AccountDetailsTable: React.FC<AccountDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftAccountDetailsRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: AccountDetailsInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    // increment sno for rows below
    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updateAccountDetailsRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addAccountDetailsRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    // decrement sno for rows below
    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updateAccountDetailsRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deleteAccountDetailsRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: string) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updateAccountDetailsRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<AccountDetailsInfoRow>[] = [
    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      isEditable: false,
      className: "w-[50px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Name of Account",
        field: "name_of_account",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.name_of_account ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_account"),
      },

      {
        headerName: "Type of Account",
        field: "type_of_account",
        type: "select" as ColumnType,
        isEditable: true,
        options: ACCOUNT_TYPE_OPTIONS,
        getValue: (row) => row.type_of_account ?? null,
        onChange: (rowIndex: number, value: AccountDetailsAccountType) =>
          handleChange(rowIndex, value, "type_of_account"),
      },

      {
        headerName: "Others (Please specify)",
        field: "others_specify",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.others_specify ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "others_specify"),
      },

      {
        headerName: "Account Address",
        field: "account_address",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.account_address ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "account_address"),
      },

      {
        headerName: "Date of Sanction",
        field: "date_of_sanction",
        type: "date" as ColumnType,
        isEditable: true,
        getValue: (row) => row.date_of_sanction ?? null,
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "date_of_sanction"),
      },

      {
        headerName: "Borrowal Account Sr. No.",
        field: "borrowal_account_sr_no",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.borrowal_account_sr_no ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "borrowal_account_sr_no"),
      },

      {
        headerName: "Sanctioned Limit",
        field: "sanctioned_limit",
        type: "number" as ColumnType,
        isEditable: true,
        getValue: (row) => row.sanctioned_limit ?? null,
        onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "sanctioned_limit"),
      },

      {
        headerName: "Balance Outstanding",
        field: "balance_outstanding",
        type: "number" as ColumnType,
        isEditable: true,
        getValue: (row) => row.balance_outstanding ?? null,
        onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "balance_outstanding"),
      },

      {
        headerName: "PAN of Account",
        field: "pan_of_account",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.pan_of_account ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "pan_of_account"),
      },

      {
        headerName: "CIN",
        field: "cin",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.cin ?? "",
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "cin"),
      },

      {
        headerName: "Legal Entity Identifier (LEI)",
        field: "legal_entity_identifier",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.legal_entity_identifier ?? "",
        onChange: (rowIndex: number, value: string) =>
          handleChange(rowIndex, value, "legal_entity_identifier"),
      },

      {
        headerName: "Date of NPA",
        field: "date_of_npa_if_any",
        type: "date" as ColumnType,
        isEditable: true,
        getValue: (row) => row.date_of_npa_if_any ?? null,
        onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "date_of_npa_if_any"),
      },

      {
        headerName: "Customer ID",
        field: "name_of_account_customer_id",
        type: "text" as ColumnType,
        isEditable: true,
        getValue: (row) => row.name_of_account_customer_id ?? "",
        onChange: (rowIndex: number, value: string) =>
          handleChange(rowIndex, value, "name_of_account_customer_id"),
      },
    ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Account Details"}
    />
  );
};

AccountDetailsTable.displayName = "AccountDetailsTable";
